/**
 * @module features/devices/components/TelemetryGaugesGrid
 * @description Four metric cards: CPU gauge, Memory gauge, WiFi signal + SSID match
 * status, and Hardware/OS integrity (MAC, OS hash, received timestamp). Colour is
 * never the only signal: each gauge also prints its percentage and a severity word.
 */
import * as React from "react";
import { Cpu, MemoryStick, ShieldCheck, ShieldAlert, Wifi } from "lucide-react";
import { cn } from "@/lib/utils";
import { DeviceTelemetryDashboardData, DisplayModuleTelemetryLog, WifiStrength } from "../types/telemetry";
import { formatDateTimeSeconds, formatRelativeTime, formatUtc, truncateMiddle } from "../utils/format";
import { GAUGE_CARD_HEIGHT } from "./TelemetryDashboardSkeleton";

export type Severity = "normal" | "warning" | "critical";

/** normal < 70, warning 70–89, critical >= 90 (research.md §5). */
export function severityFor(value: number): Severity {
  if (value >= 90) return "critical";
  if (value >= 70) return "warning";
  return "normal";
}

const SEVERITY_STYLE: Record<Severity, { stroke: string; text: string; label: string }> = {
  normal: { stroke: "stroke-emerald-500", text: "text-emerald-600 dark:text-emerald-400", label: "Normal" },
  warning: { stroke: "stroke-amber-500", text: "text-amber-600 dark:text-amber-400", label: "Warning" },
  critical: { stroke: "stroke-red-500", text: "text-red-600 dark:text-red-400", label: "Critical" },
};

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section
      aria-label={title}
      className={cn("rounded-xl border border-border/80 bg-card p-4 shadow-xs flex flex-col", GAUGE_CARD_HEIGHT)}
    >
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {icon}
        {title}
      </h3>
      <div className="flex-1 min-h-0 mt-2">{children}</div>
    </section>
  );
}

/** 270° arc gauge drawn with two SVG circles (track + value). */
export function ArcGauge({ value, label }: { value: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, value));
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const arc = circumference * 0.75;
  const sev = severityFor(clamped);
  const style = SEVERITY_STYLE[sev];
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-28 w-28" role="img" aria-label={`${label} ${clamped.toFixed(1)} percent, ${style.label}`}>
        <circle
          cx="50" cy="50" r={radius} fill="none" strokeWidth="9" strokeLinecap="round"
          className="stroke-muted" strokeDasharray={`${arc} ${circumference}`} transform="rotate(135 50 50)"
        />
        <circle
          cx="50" cy="50" r={radius} fill="none" strokeWidth="9" strokeLinecap="round"
          className={cn("transition-[stroke-dasharray] duration-500", style.stroke)}
          strokeDasharray={`${(arc * clamped) / 100} ${circumference}`} transform="rotate(135 50 50)"
        />
        <text x="50" y="54" textAnchor="middle" className="fill-foreground text-[18px] font-semibold">
          {clamped.toFixed(0)}%
        </text>
      </svg>
      <span className={cn("text-[11px] font-semibold", style.text)}>{style.label}</span>
    </div>
  );
}

const WIFI_BARS: Record<WifiStrength, number> = { low: 1, medium: 2, high: 3 };

function WifiBars({ strength }: { strength: WifiStrength }) {
  const active = WIFI_BARS[strength];
  return (
    <div className="flex items-end gap-1 h-8" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={cn("w-2 rounded-sm", n <= active ? "bg-emerald-500" : "bg-muted")}
          style={{ height: `${n * 33}%` }}
        />
      ))}
    </div>
  );
}

interface TelemetryGaugesGridProps {
  device: DeviceTelemetryDashboardData["device"];
  assignment: DeviceTelemetryDashboardData["assignment"];
  latest: DisplayModuleTelemetryLog;
}

export function TelemetryGaugesGrid({ device, latest }: TelemetryGaugesGridProps) {
  const macMatches =
    !!device.mac_address && latest.mac_id.toUpperCase() === device.mac_address.toUpperCase();
  const osMatches = !device.expected_os_hash || device.expected_os_hash === latest.os_hash;
  const wifiMatched = latest.resolved_system_wifi_id !== null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <Card title="CPU load" icon={<Cpu className="h-3.5 w-3.5" aria-hidden="true" />}>
        <ArcGauge value={latest.cpu_load} label="CPU load" />
      </Card>

      <Card title="Memory" icon={<MemoryStick className="h-3.5 w-3.5" aria-hidden="true" />}>
        <ArcGauge value={latest.memory_load_percentage} label="Memory load" />
        <p className="-mt-1 text-center text-[11px] text-muted-foreground">
          {latest.memory_available.toFixed(0)} MB free of {latest.memory_total.toFixed(0)} MB
        </p>
      </Card>

      <Card title="WiFi" icon={<Wifi className="h-3.5 w-3.5" aria-hidden="true" />}>
        <div className="flex h-full flex-col justify-center gap-2">
          <div className="flex items-center gap-3">
            <WifiBars strength={latest.wifi_strength} />
            <div className="min-w-0">
              <p className="text-sm font-semibold capitalize">{latest.wifi_strength}</p>
              <p className="text-[11px] font-mono text-muted-foreground">{latest.wifi_rssi}</p>
            </div>
          </div>
          <p className="truncate text-xs font-medium" title={latest.connected_wifi_name}>
            {latest.connected_wifi_name || "No SSID reported"}
          </p>
          <span
            className={cn(
              "inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
              wifiMatched
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60"
                : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60"
            )}
          >
            {wifiMatched ? "Matches system profile" : "Unrecognized network"}
          </span>
        </div>
      </Card>

      <Card
        title="Hardware & OS"
        icon={
          macMatches && osMatches ? (
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
          )
        }
      >
        <dl className="space-y-1.5 text-[11px]">
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">MAC</dt>
            <dd className="font-mono">
              {latest.mac_id} <span className="font-sans font-semibold">{macMatches ? "✓ verified" : "✗ mismatch"}</span>
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">OS hash</dt>
            <dd className="font-mono" title={latest.os_hash}>
              {truncateMiddle(latest.os_hash, 8, 4)}{" "}
              <span className="font-sans font-semibold">{osMatches ? "✓" : "differs"}</span>
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">Received</dt>
            <dd title={formatUtc(latest.received_timestamp)} className="text-right">
              {formatDateTimeSeconds(latest.received_timestamp)}
              <span className="block text-muted-foreground">{formatRelativeTime(latest.received_timestamp)}</span>
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

